/**
 * Procedural planet textures drawn on canvas: no external assets, deterministic.
 *
 * Ported from prototypes/solar-system/src/textures.js. Kept faithful on
 * purpose -- these are tuned values that looked right on a Quest, and
 * "improving" them would be regressing tested output. The seeds are fixed, so
 * every headset in a class renders the same planet.
 */
import * as THREE from 'three';

type Rgb = [number, number, number];
type Rgba = [number, number, number, number];

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

type Noise3 = (x: number, y: number, z: number) => number;

function makeNoise(seed: number): Noise3 {
  const r = rng(seed);
  const N = 256;
  const perm = new Uint8Array(N * 2);
  const grad = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    perm[i] = i;
    grad[i] = r();
  }
  for (let i = N - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    const tmp = perm[i]!;
    perm[i] = perm[j]!;
    perm[j] = tmp;
  }
  for (let i = 0; i < N; i++) perm[i + N] = perm[i]!;

  const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

  // 3D value noise sampled on the sphere, so the texture has no seam.
  return (x, y, z) => {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    const Z = Math.floor(z) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    z -= Math.floor(z);
    const u = fade(x);
    const v = fade(y);
    const w = fade(z);
    const g = (a: number, b: number, c: number): number => grad[perm[perm[perm[a]! + b]! + c]!]!;
    return lerp(
      lerp(lerp(g(X, Y, Z), g(X + 1, Y, Z), u), lerp(g(X, Y + 1, Z), g(X + 1, Y + 1, Z), u), v),
      lerp(
        lerp(g(X, Y, Z + 1), g(X + 1, Y, Z + 1), u),
        lerp(g(X, Y + 1, Z + 1), g(X + 1, Y + 1, Z + 1), u),
        v,
      ),
      w,
    );
  };
}

function fbm(
  noise: Noise3,
  x: number,
  y: number,
  z: number,
  oct = 5,
  lac = 2.1,
  gain = 0.5,
): number {
  let a = 0;
  let amp = 1;
  let f = 1;
  let norm = 0;
  for (let i = 0; i < oct; i++) {
    a += amp * noise(x * f, y * f, z * f);
    norm += amp;
    amp *= gain;
    f *= lac;
  }
  return a / norm;
}

type Sampler = (x: number, y: number, z: number, lat: number, lon: number) => Rgb | Rgba;

/** Draws an equirectangular map by sampling a function of (unit vector, lat, lon). */
function equirect(w: number, h: number, fn: Sampler): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const d = img.data;
  for (let j = 0; j < h; j++) {
    const lat = (0.5 - j / h) * Math.PI;
    const cl = Math.cos(lat);
    const sl = Math.sin(lat);
    for (let i = 0; i < w; i++) {
      const lon = (i / w) * Math.PI * 2;
      const c = fn(cl * Math.cos(lon), sl, cl * Math.sin(lon), lat, lon);
      const k = (j * w + i) * 4;
      d[k] = c[0];
      d[k + 1] = c[1];
      d[k + 2] = c[2];
      d[k + 3] = c[3] === undefined ? 255 : c[3];
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

const mix = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
const clamp01 = (t: number): number => Math.max(0, Math.min(1, t));

export function earthTexture(w = 1024, h = 512): THREE.CanvasTexture {
  const n = makeNoise(11);
  const n2 = makeNoise(12);
  return equirect(w, h, (x, y, z, lat) => {
    const e = fbm(n, x * 2.2, y * 2.2, z * 2.2, 6) + 0.15 * fbm(n2, x * 8, y * 8, z * 8, 3) - 0.53;
    const ice = Math.abs(lat) > 1.25 ? clamp01((Math.abs(lat) - 1.25) * 6) : 0;
    let c: Rgb;
    if (e < 0) {
      c = mix([40, 110, 190], [8, 30, 90], clamp01(-e * 6));
    } else {
      const t = clamp01(e * 7);
      const dry = 0.5 + 0.5 * fbm(n2, x * 3, y * 3, z * 3, 3) - Math.abs(lat) * 0.25;
      const low = mix([60, 120, 50], [150, 130, 80], clamp01(dry));
      c = mix(low, [120, 110, 95], t);
      if (t > 0.8) c = mix(c, [225, 225, 225], (t - 0.8) * 5);
    }
    return mix(c, [235, 240, 245], ice);
  });
}

export function cloudTexture(w = 1024, h = 512): THREE.CanvasTexture {
  const n = makeNoise(21);
  return equirect(w, h, (x, y, z) => {
    const c = fbm(n, x * 3, y * 3, z * 3, 5) + 0.2 * fbm(n, x * 12, y * 12, z * 12, 3);
    return [255, 255, 255, Math.floor(clamp01((c - 0.55) * 4) * 255)];
  });
}

export function moonTexture(w = 512, h = 256): THREE.CanvasTexture {
  const n = makeNoise(31);
  const r = rng(32);
  const craters = Array.from({ length: 90 }, () => {
    const v = new THREE.Vector3().randomDirection();
    return [v.x, v.y, v.z, 0.03 + r() * 0.12] as const;
  });
  return equirect(w, h, (x, y, z) => {
    let v =
      0.55 +
      0.25 * fbm(n, x * 4, y * 4, z * 4, 4) -
      0.3 * clamp01(fbm(n, x * 1.5 + 3, y * 1.5, z * 1.5, 3) - 0.5);
    for (const [cx, cy, cz, rad] of craters) {
      const d = Math.acos(clamp01(x * cx + y * cy + z * cz) * 0.99999);
      if (d < rad) {
        const t = d / rad;
        v += t > 0.8 ? 0.18 : -0.12 * (1 - t);
      }
    }
    const g = Math.floor(clamp01(v) * 200 + 30);
    return [g, g, g - 6];
  });
}

export function marsTexture(w = 1024, h = 512): THREE.CanvasTexture {
  const n = makeNoise(41);
  const n2 = makeNoise(42);
  return equirect(w, h, (x, y, z, lat) => {
    const e = fbm(n, x * 2.5, y * 2.5, z * 2.5, 6);
    const dark = clamp01((fbm(n2, x * 1.8, y * 1.8, z * 1.8, 4) - 0.5) * 4);
    let c = mix([200, 110, 60], [150, 75, 40], e);
    c = mix(c, [95, 55, 40], dark * 0.7);
    const cap = clamp01((Math.abs(lat) - 1.3) * 8 + 0.25 * fbm(n2, x * 6, y * 6, z * 6, 2));
    return mix(c, [240, 235, 230], cap);
  });
}

export const jupiterBands: Rgb[] = [
  [200, 180, 150],
  [225, 205, 175],
  [170, 130, 100],
  [230, 215, 190],
  [180, 140, 105],
  [235, 220, 195],
  [160, 120, 95],
  [220, 200, 170],
  [200, 175, 145],
];

export const saturnBands: Rgb[] = [
  [200, 180, 130],
  [225, 205, 155],
  [235, 220, 175],
  [215, 195, 145],
  [240, 225, 185],
  [225, 205, 155],
  [205, 185, 135],
];

export function gasTexture(
  bands: Rgb[],
  seed: number,
  w = 1024,
  h = 512,
  turbulence = 0.08,
): THREE.CanvasTexture {
  const n = makeNoise(seed);
  return equirect(w, h, (x, y, z, lat) => {
    const t =
      lat / Math.PI +
      0.5 +
      turbulence * (fbm(n, x * 3, y * 6, z * 3, 4) - 0.5) +
      0.02 * (fbm(n, x * 10, y * 30, z * 10, 3) - 0.5);
    const p = clamp01(t) * (bands.length - 1);
    const i = Math.floor(p);
    const c = mix(bands[i]!, bands[Math.min(i + 1, bands.length - 1)]!, p - i);
    // Jupiter (seed 51) gets the Great Red Spot.
    const storm =
      seed === 51 ? clamp01(1 - Math.hypot((lat + 0.35) * 5, (Math.atan2(z, x) - 0.8) * 2.2)) : 0;
    return mix(c, [190, 90, 60], storm > 0 ? Math.pow(storm, 1.5) : 0);
  });
}

export function sunTexture(w = 512, h = 256): THREE.CanvasTexture {
  const n = makeNoise(61);
  const r = rng(62);
  const spots = Array.from({ length: 8 }, () => {
    const v = new THREE.Vector3().randomDirection();
    v.y *= 0.45;
    v.normalize();
    return [v.x, v.y, v.z, 0.035 + r() * 0.06] as const;
  });
  return equirect(w, h, (x, y, z) => {
    const g = 0.5 + 0.5 * fbm(n, x * 7, y * 7, z * 7, 5, 2.3, 0.55);
    let c = mix([230, 90, 10], [255, 215, 120], g);
    for (const [cx, cy, cz, rad] of spots) {
      const d = Math.acos(clamp01(x * cx + y * cy + z * cz) * 0.99999);
      if (d < rad) {
        const t = d / rad;
        c = mix(c, t < 0.5 ? [60, 20, 5] : [160, 60, 10], t < 0.5 ? 1 : (1 - t) * 2);
      }
    }
    return c;
  });
}

export function ringTexture(w = 512): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = 4;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(w, 4);
  const d = img.data;
  const r = rng(71);
  const gaps: [number, number][] = [
    [0.42, 0.46],
    [0.63, 0.645],
    [0.86, 0.88],
  ];
  let v = 0.7;
  for (let i = 0; i < w; i++) {
    const t = i / w;
    v = Math.max(0.3, Math.min(1, v + (r() - 0.5) * 0.3));
    let a = 0.85 * v;
    if (t < 0.05) a *= t / 0.05;
    if (t > 0.95) a *= (1 - t) / 0.05;
    for (const [g0, g1] of gaps) if (t > g0 && t < g1) a *= 0.08;
    const c = mix([200, 185, 150], [235, 225, 200], v);
    for (let j = 0; j < 4; j++) {
      const k = (j * w + i) * 4;
      d[k] = c[0];
      d[k + 1] = c[1];
      d[k + 2] = c[2];
      d[k + 3] = Math.floor(a * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function glowSprite(
  size = 256,
  inner: Rgb = [255, 220, 150],
  outer: Rgb = [255, 120, 20],
): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, `rgba(${inner.join(',')},1)`);
  g.addColorStop(0.35, `rgba(${outer.join(',')},0.55)`);
  g.addColorStop(1, `rgba(${outer.join(',')},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
